import React from 'react';
import { List } from 'immutable';
import { SelfExclusionTab } from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/ReactivateSelfExclusion/SelfExclusionTab';
import { SrgQuestionnaire } from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/SrgQuestionnaire/SrgQuestionnaire';
import {
    ResponsibleGamblingTabItems,
    ResponsibleGamblingTabItemsRoute,
} from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/ResponsibleGamblingTabs';
import { DepositLimitTab } from 'src/components/DepositLimitTab/DepositLimitTab';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { PlayBreakTab } from 'src/components/PlayBreakForm/PlayBreakTab';
import { ComplianceRestriction } from './ComplianceRestriction/ComplianceRestriction';
import { ResponsibleGamblingTabsState } from './ResponsibleGamblingTabs.state';
import { RealityCheckTab } from 'src/components/RealityCheckForm/RealityCheckTab';
import { DepositRestriction } from './DepositRestrictionTab/DepositRestrictionTab';
import { MarkersOfHarm } from 'src/components/MarkersOfHarm/MarkersOfHarm';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { GamprotectTab } from './Gamprotect/Gamprotect';
import { AccountConfig } from 'src/components/LicenseConfig/AccountConfig/AccountConfig';
import { Permission } from 'src/AppState/PermissionGrants/PermissionGrants.types';
import { GamstopTab } from './Gamstop/Gamstop';

export interface ResponsibleGamblingTabPropsType {
    id: number;
    permissionGrants: List<unknown>;
}

export const ResponsibleGamblingTab = observer(
    'ResponsibleGamblingTab',
    ({ id, permissionGrants }: ResponsibleGamblingTabPropsType) => {
        const common = useCommon();
        const {
            config: {
                srgEnabled: isSrgEnabled,
                depositRestriction,
                markersOfHarm: isMarkersOfHarm,
                gamprotect,
                idMission,
            },
        } = ConfigState.get(common);
        const { routerBackoffice, models, permissionGrantsState } = useAppStateContext();

        const [state] = React.useState(() => new ResponsibleGamblingTabsState(id, permissionGrants, models));
        const customerParams =
            routerBackoffice.getParametersString('customers', 'tab.tab.tab') ??
            ResponsibleGamblingTabItemsRoute.DEPOSITLIMITS;
        const isGamprotectEnabled = gamprotect !== false;
        const hasComplianceConfigPermission = permissionGrantsState.has(Permission.complianceComplianceConfig);
        const isAccountConfigEnabled = hasComplianceConfigPermission && idMission;

        return (
            <>
                <ResponsibleGamblingTabItems
                    isSrgVisible={isSrgEnabled}
                    depositRestriction={depositRestriction}
                    isMarkersOfHarm={isMarkersOfHarm}
                    isGamprotectEnabled={isGamprotectEnabled}
                    isAccountConfigEnabled={isAccountConfigEnabled}
                />

                {customerParams === ResponsibleGamblingTabItemsRoute.DEPOSITLIMITS && (
                    <DepositLimitTab
                        key={ResponsibleGamblingTabItemsRoute.DEPOSITLIMITS}
                        label='Deposit limits'
                        dashboard='gross'
                        customerId={id}
                        permissionDepositLimits={state.getPermission('banking/deposit-limits')}
                        currency={state.currency}
                    />
                )}

                {customerParams === ResponsibleGamblingTabItemsRoute.NETDEPOSITLIMITS && (
                    <DepositLimitTab
                        key={ResponsibleGamblingTabItemsRoute.NETDEPOSITLIMITS}
                        label='Net deposit limits'
                        dashboard='net'
                        customerId={id}
                        permissionDepositLimits={state.getPermission('banking/deposit-limits')}
                        currency={state.currency}
                    />
                )}

                {customerParams === ResponsibleGamblingTabItemsRoute.SELFEXCLUSION && (
                    <SelfExclusionTab
                        key={ResponsibleGamblingTabItemsRoute.SELFEXCLUSION}
                        customerId={id}
                        permissionSelfExclusion={state.getPermission('accounts/self-exclusion')}
                    />
                )}

                {customerParams === ResponsibleGamblingTabItemsRoute.PLAYBRAKE && (
                    <PlayBreakTab
                        key={ResponsibleGamblingTabItemsRoute.PLAYBRAKE}
                        customerId={id}
                        permissionAccountsPlayBreak={state.getPermission('accounts/play-break')}
                    />
                )}

                {customerParams === ResponsibleGamblingTabItemsRoute.REALITYCHECK && (
                    <RealityCheckTab
                        key={ResponsibleGamblingTabItemsRoute.REALITYCHECK}
                        customerId={id}
                        permissionRealityCheck={state.getPermission('accounts/reality-check')}
                    />
                )}

                {customerParams === ResponsibleGamblingTabItemsRoute.COMPLIANCERESTRICTION && isSrgEnabled && (
                    <ComplianceRestriction
                        key={ResponsibleGamblingTabItemsRoute.COMPLIANCERESTRICTION}
                        customerId={id}
                    />
                )}

                {customerParams === ResponsibleGamblingTabItemsRoute.DEPOSITRESTRICTION && depositRestriction && (
                    <DepositRestriction
                        key={ResponsibleGamblingTabItemsRoute.DEPOSITRESTRICTION}
                        customerId={id}
                    />
                )}

                {customerParams === ResponsibleGamblingTabItemsRoute.SRGQUESTIONS && isSrgEnabled && (
                    <SrgQuestionnaire
                        key={ResponsibleGamblingTabItemsRoute.SRGQUESTIONS}
                        customerId={id}
                    />
                )}
                {customerParams === ResponsibleGamblingTabItemsRoute.MARKERSOFHARM && isMarkersOfHarm && (
                    <MarkersOfHarm
                        key={ResponsibleGamblingTabItemsRoute.MARKERSOFHARM}
                        customerId={id}
                        currency={state.currency}
                    />
                )}
                {customerParams === ResponsibleGamblingTabItemsRoute.GAMPROTECT && isGamprotectEnabled && (
                    <GamprotectTab
                        key={ResponsibleGamblingTabItemsRoute.GAMPROTECT}
                        customerId={id}
                    />
                )}
                {customerParams === ResponsibleGamblingTabItemsRoute.GAMSTOP && (
                    <GamstopTab
                        key={ResponsibleGamblingTabItemsRoute.GAMSTOP}
                        customerId={id}
                    />
                )}
                {customerParams === ResponsibleGamblingTabItemsRoute.ACCOUNTCONFIG && isAccountConfigEnabled && (
                    <AccountConfig
                        key={ResponsibleGamblingTabItemsRoute.ACCOUNTCONFIG}
                        customerId={id}
                    />
                )}
            </>
        );
    }
);
